// Pharmacy payment-audit PDF exports.
//
// Builds the auditor-ready "back-up of drug payments" documents for ODB
// and NIHB scopes. Each row shows: Patient | Billing date | Drug | Qty
// (bottles). The math behind "Qty" honours the contract recently nailed
// down with the user:
//   • ODB  = 4 weekly claims billed per bottle (every claim = 1/4 bottle).
//            Aggregated per (patient, drug, cycle) → typically 1 bottle
//            per row; can be 2/3 if a patient overlaps multiple cycles.
//   • NIHB = 1 bottle = 1 claim. Aggregated per (patient, drug, NIHB
//            payment cycle) so the ledger reconciles 1:1 with the
//            on-screen NIHB Cycle cards. Bottles cannot be split into
//            fractions (a critical rule the user re-confirmed).
//
// The PDF is generated entirely client-side with jsPDF + autoTable so we
// don't have to round-trip the data through the backend.

import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import {
  hcvDrugKey,
  buildPatientPayerMap,
  unitsPerClaimForPayer,
  canonicalDrugName,
  HCV_COST_PER_UNIT,
} from "@/lib/hcv";
import {
  formatShortDate,
  formatPayDate,
  formatCurrency,
  addMonths,
  lastDayOfMonth,
  nihbCycleForServiceDate,
  COPAY_WAIVED_PER_DISPENSING,
} from "@/lib/dashboard-utils";

// Manufacturer/supplier each HCV drug is purchased from. Gilead supplies
// the three direct-acting antivirals (Epclusa/Vosevi/Harvoni); Sentrex
// supplies Maviret. The user audits each supplier independently, so
// Maviret rows are pushed to their own sheet (separate page) with their
// own totals — that page-1 total = amount owed to Gilead, page-2 total
// = amount owed to Sentrex. Non-HCV drugs come from many wholesalers and
// have no $20K anchor, so their acquisition cost shows as "—".
const SUPPLIER_BY_DRUG_KEY = {
  EPCLUSA: "Gilead",
  VOSEVI: "Gilead",
  HARVONI: "Gilead",
  MAVIRET: "Sentrex",
};
function acquisitionCostFor(drugName, bottles) {
  // null = unknown (non-HCV); auditor sees "—" in the cell.
  if (!hcvDrugKey(drugName)) return null;
  return (bottles || 0) * HCV_COST_PER_UNIT;
}

// Commission fees the pharmacy pays out per bottle when the audit ledger
// is split by program. Each program has a flat per-bottle rate. The DB
// still stores the legacy keys "420" / "HepCURE" for backward compat
// with existing patient tags, but every user-facing label (dropdown,
// subtitle, ledger sheet title) now uses the human staff name.
//   - 420     → George  · $50/bottle
//   - HepCURE → Racquel · $140/bottle
//   - Malachi (new)     · $100/bottle
//   - Maria   (new)     · $100/bottle
// Non-HCV drugs never carry a program commission.
const PROGRAM_COMMISSION_PER_BOTTLE = {
  "420": 50,
  "HepCURE": 140,
  "Malachi": 100,
  "Maria": 100,
};
const PROGRAM_DISPLAY_NAME = {
  "420": "George",
  "HepCURE": "Racquel",
  "Malachi": "Malachi",
  "Maria": "Maria",
};
function commissionModeFor(program /* , _staff420Unused */) {
  const rate = PROGRAM_COMMISSION_PER_BOTTLE[program];
  if (rate == null) return null;
  const display = PROGRAM_DISPLAY_NAME[program] || program;
  return {
    label: "Fees",
    subtitle: `${display} · $${rate.toFixed(2)}/bottle`,
    perBottleFn: (drug) => (hcvDrugKey(drug) ? rate : null),
  };
}

// Long form: "June 30, 2026". jsPDF's default helvetica handles plain
// ASCII reliably; we deliberately avoid unicode (→, em-dash) anywhere
// in the header because those glyphs render as garbage with the
// built-in font.
const MONTH_LONG = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];
function formatTodayLong() {
  const d = new Date();
  return `${MONTH_LONG[d.getMonth()]} ${d.getDate()}, ${d.getFullYear()}`;
}

// Inline implementation of "which ODB cycle does this service date belong
// to?" — ODB pays twice a month for dispensings in fixed windows:
//   Cycle A: dispensed 9–22 of month X → deposited 15th of month X+1
//   Cycle B: dispensed 23 of X → 8 of X+1 → deposited 30th of month X+1
// Returns null if the date can't be mapped (shouldn't happen for real
// dispense data, but stays defensive).
function odbCycleForServiceDate(iso) {
  if (!iso) return null;
  const [y, m, d] = iso.split("-").map(Number);
  if (!y || !m || !d) return null;
  if (d >= 9 && d <= 22) {
    // Cycle A — pay 15th of next month
    const payYm = addMonths(`${y}-${String(m).padStart(2, "0")}`, 1);
    return {
      key: `A-${y}-${String(m).padStart(2, "0")}`,
      payDate: `${payYm}-15`,
      shortLabel: `${monthLabel(m)} 9–22`,
    };
  }
  // Cycle B — pay 30th of next month for dispensings 23..end-of-month;
  // also covers 1..8 of the NEXT month (those are still cycle B paid on
  // the 30th of THAT month — i.e. one month after the dispense's month).
  if (d >= 23) {
    const payYm = addMonths(`${y}-${String(m).padStart(2, "0")}`, 1);
    const payDay = Math.min(30, Number(lastDayOfMonth(payYm).split("-")[2]));
    return {
      key: `B-${y}-${String(m).padStart(2, "0")}`,
      payDate: `${payYm}-${String(payDay).padStart(2, "0")}`,
      shortLabel: `${monthLabel(m)} 23 to ${monthLabel((m % 12) + 1)} 8`,
    };
  }
  // d in [1..8] — belongs to the PREVIOUS month's Cycle B
  const prevYm = addMonths(`${y}-${String(m).padStart(2, "0")}`, -1);
  const payDay = Math.min(30, Number(lastDayOfMonth(`${y}-${String(m).padStart(2, "0")}`).split("-")[2]));
  return {
    key: `B-${prevYm}`,
    payDate: `${y}-${String(m).padStart(2, "0")}-${String(payDay).padStart(2, "0")}`,
    shortLabel: `${monthLabel(Number(prevYm.split("-")[1]))} 23 to ${monthLabel(m)} 8`,
  };
}

function monthLabel(m) {
  return ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"][(m - 1) % 12];
}

// ---- formatters ----
const fmtIsoLong = (iso) => formatShortDate(iso) || iso || "—";

const fmtRangeLabel = (start, end) => {
  if (!start || !end) return "Full report";
  if (start === end) return fmtIsoLong(start);
  return `${fmtIsoLong(start)} to ${fmtIsoLong(end)}`;
};

// Build the "Period" line for the ODB ledger. Lists the unique ODB
// cycle pay dates the report covers, in chronological order, with a
// short dispense-window descriptor next to each — exactly what the
// auditor cares about (which cycle deposit(s) the bottles belong to).
// Falls back to the plain rangeStart/rangeEnd window if no cycles were
// resolved (e.g. report contained zero rows).
function buildOdbPeriodLabel(rows, rangeStart, rangeEnd) {
  if (!rows || rows.length === 0) return fmtRangeLabel(rangeStart, rangeEnd);
  const seen = new Map();
  for (const r of rows) {
    if (!r.cyclePayDate) continue;
    if (!seen.has(r.cyclePayDate)) {
      seen.set(r.cyclePayDate, r.cycleLabel || "");
    }
  }
  const cycles = [...seen.entries()].sort((a, b) => a[0].localeCompare(b[0]));
  if (cycles.length === 0) return fmtRangeLabel(rangeStart, rangeEnd);
  return cycles
    .map(([payDate, label]) =>
      label ? `${formatPayDate(payDate)} cycle (${label})` : `${formatPayDate(payDate)} cycle`
    )
    .join(" / ");
}

// Build the "Period" line for the NIHB ledger — same shape as the ODB
// header so the two exports read identically. Lists the unique NIHB
// cycle pay dates the report covers, in chronological order, with the
// dispense window next to each. Replaces the old boxed "NIHB PAYMENT
// CYCLE" banner that used to sit between the header and the table.
function buildNihbPeriodLabel(rows, rangeStart, rangeEnd) {
  if (!rows || rows.length === 0) return fmtRangeLabel(rangeStart, rangeEnd);
  const seen = new Map();
  for (const r of rows) {
    if (!r.nihbCyclePayDate) continue;
    if (!seen.has(r.nihbCyclePayDate)) {
      seen.set(r.nihbCyclePayDate, r.nihbCycleLabel || "");
    }
  }
  const cycles = [...seen.entries()].sort((a, b) => a[0].localeCompare(b[0]));
  if (cycles.length === 0) return fmtRangeLabel(rangeStart, rangeEnd);
  return cycles
    .map(([payDate, label]) =>
      label ? `${formatPayDate(payDate)} cycle (${label})` : `${formatPayDate(payDate)} cycle`
    )
    .join(" / ");
}

// Common header + footer drawer used by both ODB and NIHB exports.
//
// Layout (millimetres, Letter format):
//   y =  0 → 14   green brand bar with brand mark inside
//   y = 24        Document title (large, dark green)
//   y = 32        Subtitle (mid-grey)
//   y = 41        Period · bold dark-green
//   y = 48        Generated timestamp · mid-grey
//   table starts at startY = 58 — gives ~10mm breathing room.
function drawHeader(doc, title, subtitle, rangeLabel) {
  const pageWidth = doc.internal.pageSize.getWidth();
  // Brand bar
  doc.setFillColor("#1B3B2E");
  doc.rect(0, 0, pageWidth, 14, "F");
  doc.setTextColor("#FFFFFF");
  doc.setFont("helvetica", "bold");
  doc.setFontSize(10);
  doc.text("PHARMACY BUSINESS ANALYZER", 14, 9);
  // Title block — give the title its own row so it never collides with
  // either the brand bar or the subtitle (user reported the previous
  // header looked compressed).
  doc.setTextColor("#1B3B2E");
  doc.setFont("helvetica", "bold");
  doc.setFontSize(16);
  doc.text(title, 14, 24);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(10);
  doc.setTextColor("#5C746A");
  doc.text(subtitle, 14, 32);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(10);
  doc.setTextColor("#1B3B2E");
  doc.text(`Period: ${rangeLabel}`, 14, 41);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(9);
  doc.setTextColor("#5C746A");
  doc.text(`Generated: ${new Date().toLocaleString("en-CA")}`, 14, 48);
}

function drawFooter(doc) {
  const pageCount = doc.internal.getNumberOfPages();
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  for (let i = 1; i <= pageCount; i++) {
    doc.setPage(i);
    doc.setFontSize(8);
    doc.setTextColor("#8A9C93");
    doc.text(
      `Confidential — pharmacy audit record · Page ${i} of ${pageCount}`,
      pageWidth / 2,
      pageHeight - 8,
      { align: "center" }
    );
  }
}

// Build the ODB ($ Plan-1) ledger.
//
// Two code paths depending on whether the row is HCV or not:
//   • HCV  → bottle math by ODB cycle. Group by (patient, drug, cycle);
//            bottles = ceil(claims × 1/4) (4 weekly claims per bottle).
//            Cycle pay date = the day ODB deposits the money.
//   • Non-HCV (pharmacy) → no bottle anchor. Group by (patient, drug,
//            service_date); qty = dispensing count for that row. Each
//            dispensing is its own line so the audit list matches the
//            raw billing exactly.
//
// payerMap only classifies HCV patients (the heuristic uses a $6.5K /
// claim threshold that's HCV-specific). Unmapped patients (i.e. all
// non-HCV) default to ODB — so when the user runs the export against a
// non-HCV scope the rows still flow through this builder.
function buildOdbRows(transactions, payerMap) {
  const upc = unitsPerClaimForPayer("ODB");
  const groups = new Map();
  for (const t of transactions) {
    if (!t.patient || !t.drug_name) continue;
    // Default non-HCV / unmapped patients to ODB. NIHB-classified HCV
    // patients are filtered out and routed to the NIHB ledger.
    const payer = payerMap.get(t.patient) || "ODB";
    if (payer !== "ODB") continue;
    if (!t.service_date) continue;

    if (hcvDrugKey(t.drug_name)) {
      // HCV path — bottle math by ODB cycle. Also track markup + net
      // fees per (patient, drug, cycle) so program sheets can show
      // the "Fees" column (professional fees paid on this patient's
      // dispensings, net of the $2 co-pay waiver).
      const cycle = odbCycleForServiceDate(t.service_date);
      if (!cycle) continue;
      const key = `HCV||${t.patient}||${t.drug_name}||${cycle.key}`;
      const slot = groups.get(key) || {
        patient: t.patient,
        drug: t.drug_name,
        cycleKey: cycle.key,
        cyclePayDate: cycle.payDate,
        cycleLabel: cycle.shortLabel,
        claims: 0,
        firstDispense: t.service_date,
        isHcv: true,
        sumMarkup: 0,
        sumNetFee: 0,
      };
      slot.claims += 1;
      slot.sumMarkup += t.markup || 0;
      slot.sumNetFee += Math.max(0, (t.fees || 0) - COPAY_WAIVED_PER_DISPENSING);
      if (t.service_date < slot.firstDispense) slot.firstDispense = t.service_date;
      groups.set(key, slot);
    } else {
      // Non-HCV path — 1 row per (patient, drug, service_date),
      // qty = dispensing count on that date. Also carry running sums
      // of markup and net dispensing fees ($2 co-pay waiver deducted
      // per paid claim, matching the dashboard's Net Paid math) so the
      // non-HCV ledger can show what actually landed in the pharmacy
      // instead of an acquisition-cost anchor that doesn't apply.
      const key = `NONHCV||${t.patient}||${t.drug_name}||${t.service_date}`;
      const slot = groups.get(key) || {
        patient: t.patient,
        drug: t.drug_name,
        cyclePayDate: null,
        cycleLabel: null,
        claims: 0,
        firstDispense: t.service_date,
        isHcv: false,
        sumMarkup: 0,
        sumNetFee: 0,
      };
      slot.claims += 1;
      slot.sumMarkup += t.markup || 0;
      slot.sumNetFee += Math.max(0, (t.fees || 0) - COPAY_WAIVED_PER_DISPENSING);
      groups.set(key, slot);
    }
  }
  // bottles = HCV bottle math (4 claims = 1 bottle, round up) OR raw
  // dispensing count for non-HCV (1 claim = 1 dispensing, no rounding).
  return [...groups.values()]
    .map((g) => ({
      ...g,
      bottles: g.isHcv ? Math.max(1, Math.ceil(g.claims * upc)) : g.claims,
    }))
    .sort((a, b) => {
      // Chronological — first billed dispense date ascending, then
      // patient/drug for stable ordering. Program sheets in particular
      // want dates read top-to-bottom in dispense order.
      if (a.firstDispense !== b.firstDispense)
        return a.firstDispense.localeCompare(b.firstDispense);
      if (a.patient !== b.patient) return a.patient.localeCompare(b.patient);
      return a.drug.localeCompare(b.drug);
    });
}

// NIHB ledger.
//
// HCV NIHB:  NIHB bills monthly (1 bottle per patient per calendar
//            month per drug), but the raw pharmacy report frequently
//            carries MULTIPLE claim line items for the same NIHB
//            bottle — same date, or spread across the month. Counting
//            each raw line item as a separate bottle inflates the
//            ledger (3 claims for one June bottle → 3 bottles → $60K
//            instead of $20K). Dedupe by (patient, drug, YYYY-MM) so
//            each month bucket = exactly 1 bottle.
// Non-HCV NIHB (rare): no bottle anchor — group by (patient, drug,
//            service_date) and qty = dispensing count for that date.
//
// Only patients explicitly classified as NIHB by buildPatientPayerMap
// (HCV per-claim heuristic) flow through. Unmapped patients are NOT
// surfaced here — they belong on the ODB ledger.
function buildNihbRows(transactions, payerMap) {
  const groups = new Map();
  for (const t of transactions) {
    if (!t.patient || !t.drug_name) continue;
    if (payerMap.get(t.patient) !== "NIHB") continue;
    if (!t.service_date) continue;

    if (hcvDrugKey(t.drug_name)) {
      // HCV NIHB — 1 bottle per (patient, drug, CALENDAR MONTH) per
      // NIHB federal rule. When multiple submissions exist in the
      // same month (duplicates or reversals), keep ONLY the primary
      // (highest plan1_paid) claim — the others don't reimburse. The
      // primary determines which NIHB payment cycle the row lands in
      // so the ledger reconciles 1:1 with the on-screen NIHB Cycle
      // cards. Also carries markup + net dispensing fees from that
      // one claim for the Fees column.
      const yyyymm = t.service_date.slice(0, 7);
      const key = `HCV||${t.patient}||${t.drug_name}||${yyyymm}`;
      const slot = groups.get(key);
      const paid = t.plan1_paid || 0;
      if (!slot) {
        const cyc = nihbCycleForServiceDate(t.service_date);
        groups.set(key, {
          patient: t.patient,
          drug: t.drug_name,
          service_date: t.service_date,
          bottles: 1,
          isHcv: true,
          sumMarkup: t.markup || 0,
          // NIHB clients have no ODB $2 co-pay — fees flow through as-is.
          sumNetFee: t.fees || 0,
          nihbCycleKey: cyc ? cyc.key : yyyymm,
          nihbCyclePayDate: cyc ? cyc.payDate : null,
          nihbCycleLabel: cyc ? cyc.shortLabel : "",
          _primaryPaid: paid,
        });
      } else if (paid > (slot._primaryPaid || 0)) {
        // Higher-value claim wins — swap in its data.
        const cyc = nihbCycleForServiceDate(t.service_date);
        slot.service_date = t.service_date;
        slot.sumMarkup = t.markup || 0;
        slot.sumNetFee = t.fees || 0;
        slot.nihbCycleKey = cyc ? cyc.key : yyyymm;
        slot.nihbCyclePayDate = cyc ? cyc.payDate : null;
        slot.nihbCycleLabel = cyc ? cyc.shortLabel : "";
        slot._primaryPaid = paid;
      }
      // Non-primary claims: silently dropped — they don't deposit.
    } else {
      // Non-HCV NIHB — 1 row per dispensing date; qty = claims. Track
      // markup + net dispensing fees per group so the ledger can show
      // what actually landed in the pharmacy.
      const key = `NONHCV||${t.patient}||${t.drug_name}||${t.service_date}`;
      const slot = groups.get(key) || {
        patient: t.patient,
        drug: t.drug_name,
        service_date: t.service_date,
        bottles: 0,
        isHcv: false,
        sumMarkup: 0,
        sumNetFee: 0,
      };
      slot.bottles += 1;
      slot.sumMarkup += t.markup || 0;
      slot.sumNetFee += Math.max(0, (t.fees || 0) - COPAY_WAIVED_PER_DISPENSING);
      groups.set(key, slot);
    }
  }
  return [...groups.values()].sort((a, b) => {
    if (a.service_date !== b.service_date)
      return a.service_date.localeCompare(b.service_date);
    if (a.patient !== b.patient) return a.patient.localeCompare(b.patient);
    return a.drug.localeCompare(b.drug);
  });
}

// Optional HCV/non-HCV scope filter — the user may want an ODB ledger
// that's HCV-only (since most audit interest is the HCV programme).
function scopeFilter(rows, scope) {
  if (scope === "hcv") {
    return rows.filter((t) => !!hcvDrugKey(t.drug_name));
  }
  if (scope === "non-hcv") {
    return rows.filter((t) => !hcvDrugKey(t.drug_name));
  }
  return rows;
}

// Render one auditor sheet (header + table) on whatever page the doc is
// currently on. Supplier-aware: the title carries the supplier tag, the
// Acquisition cost column rolls up to the bottom-line $ owed to that
// supplier. Non-HCV rows show "—" in the Acquisition cost column because
// the $20K bottle anchor only applies to the four HCV DAA products.
// Followed by a compact "Dashboard reconciliation" block so the auditor
// can match the ledger's bottle and cost totals to the dashboard.
function renderLedgerSheet(doc, { title, subtitle, periodLabel, rows, dateField, options = {} }) {
  drawHeader(doc, title, subtitle, periodLabel);

  // Non-HCV-only sheets get a specialised column set: Markup + Fees
  // (net of the $2 co-pay waiver) replace Acquisition cost, because
  // the $20K bottle anchor doesn't apply to pharmacy dispensings —
  // the auditor cares about $ collected instead.
  const hcvCount = rows.filter((r) => !!hcvDrugKey(r.drug)).length;
  const nonHcvCount = rows.length - hcvCount;
  const isNonHcvOnly = hcvCount === 0 && nonHcvCount > 0;

  // Both ODB and NIHB ledgers now share the same header treatment: the
  // "Period" line above the table lists every payment cycle covered,
  // so no floating banner is needed. The main table always starts at
  // the default y=58 breathing-room offset below the header block.
  const ledgerStartY = 58;

  if (isNonHcvOnly) {
    renderNonHcvLedgerBody(doc, rows, dateField, { startY: ledgerStartY });
  } else {
    renderHcvLedgerBody(doc, rows, dateField, { ...options, startY: ledgerStartY });
  }

  // Medication Totals goes on its OWN page so a long ledger can't push
  // the summary onto the same page as the last few patient rows (and
  // so the auditor's per-medication PO totals stay visually separated
  // from the transaction detail).
  if (rows && rows.length > 0) {
    doc.addPage();
    drawHeader(doc, `${title} — Medication Totals`, subtitle, periodLabel);
    drawDrugTotalsBlock(doc, rows, {
      isNonHcvOnly,
      maskAcquisitionCost: !!options.maskAcquisitionCost,
      commissionMode: options.commissionMode || null,
      startY: 58,
    });
    // Skip the dashboard-reconciliation footer on program-split sheets
    // (420 / HepCURE) — the reconciliation compares to the pharmacy
    // P&L which doesn't apply to a program-specific commission report.
    if (!options.commissionMode) {
      drawReconciliationFooter(doc, { rows, isNonHcvOnly });
    }
  }
}

// HCV / mixed sheet body — up to 5 columns:
//   Patient | Qty | Dispensing Date | Drug | Acquisition cost/Fees
// When `maskAcquisitionCost` is true the money column is dropped
// entirely (4-column ledger). When a `commissionMode` is supplied the
// money column becomes "Fees" (per-bottle fee to the program) instead
// of the $20K acquisition cost.
function renderHcvLedgerBody(doc, rows, dateField, opts = {}) {
  const maskAcq = !!opts.maskAcquisitionCost;
  const commission = opts.commissionMode || null;
  const startY = opts.startY != null ? opts.startY : 58;
  const moneyLabel = commission ? commission.label : "Acquisition cost";
  const moneyFor = commission
    ? (r) => {
        const rate = commission.perBottleFn(r.drug);
        if (rate == null) return null;
        return (r.bottles || 0) * rate;
      }
    : (r) => acquisitionCostFor(r.drug, r.bottles);
  const qtyHeader =
    rows.every((r) => !!hcvDrugKey(r.drug)) ? "Qty (bottles)" : "Qty";

  // Bucket rows by cycleKey UP FRONT (first-seen order preserved) so a
  // non-HCV row without a cycleKey — which can sort between two HCV
  // cycles on the mixed "All drugs" sheet — never splits a cycle into
  // multiple fragment subtotals.
  const buckets = new Map(); // cycleKey -> rows[]
  const bucketOrder = [];    // insertion order of real cycle keys
  const uncycledRows = [];
  for (const r of rows) {
    if (!r.cycleKey) {
      uncycledRows.push(r);
      continue;
    }
    if (!buckets.has(r.cycleKey)) {
      buckets.set(r.cycleKey, []);
      bucketOrder.push(r.cycleKey);
    }
    buckets.get(r.cycleKey).push(r);
  }
  // Subtotals fire ONLY when >1 real HCV cycle is present. A single
  // cycle + some non-HCV rows still emits one grand TOTAL only.
  const showSubtotals = bucketOrder.length > 1;

  const buildRow = (r) => {
    const base = [
      r.patient,
      String(r.bottles),
      fmtIsoLong(r[dateField]),
      r.drug,
    ];
    if (maskAcq) return base;
    const money = moneyFor(r);
    return [...base, money == null ? "—" : formatCurrency(money)];
  };

  const buildSubtotalRow = (cycleRows, cycleKey) => {
    const bottles = cycleRows.reduce((s, r) => s + (r.bottles || 0), 0);
    const patients = new Set(cycleRows.map((r) => r.patient)).size;
    let moneySum = 0;
    let moneyCount = 0;
    for (const r of cycleRows) {
      const a = moneyFor(r);
      if (a != null) {
        moneySum += a;
        moneyCount += 1;
      }
    }
    // Use the cycle's own label ("Cycle A · Jun 9-22") if present so
    // the auditor recognises which cycle this subtotal reconciles to.
    const anchor = cycleRows.find((r) => r.cycleLabel);
    const cycleName = anchor?.cycleLabel || cycleKey || "";
    const label = `Subtotal · ${cycleName} · ${cycleRows.length} ${cycleRows.length === 1 ? "entry" : "entries"} · ${patients} patient${patients === 1 ? "" : "s"}`;
    const subtotalStyle = {
      fontStyle: "bold",
      fillColor: [230, 235, 232],
      textColor: [27, 59, 46],
    };
    if (maskAcq) {
      return [
        { content: label, styles: { ...subtotalStyle, halign: "left" } },
        { content: String(bottles), styles: { ...subtotalStyle, halign: "center" } },
        { content: "", colSpan: 2, styles: subtotalStyle },
      ];
    }
    return [
      { content: label, styles: { ...subtotalStyle, halign: "left" } },
      { content: String(bottles), styles: { ...subtotalStyle, halign: "center" } },
      { content: "", colSpan: 2, styles: subtotalStyle },
      {
        content: moneyCount > 0 ? formatCurrency(moneySum) : "—",
        styles: { ...subtotalStyle, halign: "right" },
      },
    ];
  };

  // Body assembly — iterate cycle buckets in first-seen order and emit
  // a subtotal after each bucket when subtotals are enabled. Uncycled
  // rows (non-HCV on mixed sheets) render as a trailing group with NO
  // subtotal — they don't have cycle semantics to reconcile against.
  const body = [];
  if (showSubtotals) {
    for (const cycleKey of bucketOrder) {
      const cycleRows = buckets.get(cycleKey);
      for (const br of cycleRows) body.push(buildRow(br));
      body.push(buildSubtotalRow(cycleRows, cycleKey));
    }
    for (const br of uncycledRows) body.push(buildRow(br));
  } else {
    for (const r of rows) body.push(buildRow(r));
  }

  const totalBottles = rows.reduce((s, r) => s + (r.bottles || 0), 0);
  const uniquePatients = new Set(rows.map((r) => r.patient)).size;
  let totalMoney = 0;
  let knownMoneyRowCount = 0;
  for (const r of rows) {
    const a = moneyFor(r);
    if (a != null) {
      totalMoney += a;
      knownMoneyRowCount += 1;
    }
  }
  const totalLabel = `TOTAL · ${rows.length} ${rows.length === 1 ? "entry" : "entries"} · ${uniquePatients} patient${uniquePatients === 1 ? "" : "s"}`;
  if (maskAcq) {
    body.push([
      { content: totalLabel, styles: { fontStyle: "bold", halign: "left" } },
      { content: String(totalBottles), styles: { fontStyle: "bold", halign: "center" } },
      { content: "", colSpan: 2 },
    ]);
  } else {
    body.push([
      { content: totalLabel, styles: { fontStyle: "bold", halign: "left" } },
      { content: String(totalBottles), styles: { fontStyle: "bold", halign: "center" } },
      { content: "", colSpan: 2 },
      {
        content: knownMoneyRowCount > 0 ? formatCurrency(totalMoney) : "—",
        styles: { fontStyle: "bold", halign: "right" },
      },
    ]);
  }

  const head = maskAcq
    ? [["Patient name", qtyHeader, "Dispensing Date", "Drug"]]
    : [["Patient name", qtyHeader, "Dispensing Date", "Drug", moneyLabel]];
  // Column widths — Patient wider so long combined names like
  // "FONSECA ALMEIDA, JESSICA" no longer clip. Drug column reduced
  // (EPCLUSA / VOSEVI / MAVIRET are short brand names).
  const columnStyles = maskAcq
    ? {
        0: { cellWidth: 72 },
        1: { cellWidth: 26, halign: "center" },
        2: { cellWidth: 40 },
        3: { cellWidth: 52 },
      }
    : {
        0: { cellWidth: 60 },
        1: { cellWidth: 22, halign: "center" },
        2: { cellWidth: 32 },
        3: { cellWidth: 44 },
        4: { cellWidth: 32, halign: "right" },
      };

  autoTable(doc, {
    startY,
    head,
    body,
    styles: { fontSize: 9, cellPadding: 2.4, textColor: "#1B3B2E", overflow: "linebreak" },
    headStyles: { fillColor: "#1B3B2E", textColor: "#FFFFFF", fontStyle: "bold", halign: "left" },
    alternateRowStyles: { fillColor: "#F7F9F8" },
    columnStyles,
    // Repeat head on continuation pages only — autoTable will only
    // emit continuation pages when the body actually overflows, so
    // a single-page ledger stays single-page with one banner.
    showHead: "everyPage",
    didParseCell: (data) => {
      if (data.column.index === 1 && data.section === "head") {
        data.cell.styles.halign = "center";
      }
      if (!maskAcq && data.column.index === 4 && data.section === "head") {
        data.cell.styles.halign = "right";
      }
    },
  });
}

// Non-HCV-only sheet body — 7 columns: Markup + Fees + Net Paid replace
// Acquisition cost. Fees = raw dispensing fees minus the $2 co-pay
// waiver per paid dispensing (matches the dashboard's Net Paid).
// Net Paid = Markup + Fees, i.e. the total dollars that landed in the
// pharmacy for that row.
function renderNonHcvLedgerBody(doc, rows, dateField, opts = {}) {
  const startY = opts.startY != null ? opts.startY : 58;
  const body = rows.map((r) => {
    const mk = r.sumMarkup || 0;
    const fe = r.sumNetFee || 0;
    return [
      r.patient,
      fmtIsoLong(r[dateField]),
      r.drug,
      String(r.bottles),
      formatCurrency(mk),
      formatCurrency(fe),
      formatCurrency(mk + fe),
    ];
  });

  const totalQty = rows.reduce((s, r) => s + (r.bottles || 0), 0);
  const uniquePatientsNonHcv = new Set(rows.map((r) => r.patient)).size;
  const totalMarkup = rows.reduce((s, r) => s + (r.sumMarkup || 0), 0);
  const totalNetFee = rows.reduce((s, r) => s + (r.sumNetFee || 0), 0);
  const totalNetPaid = totalMarkup + totalNetFee;
  body.push([
    { content: `TOTAL · ${rows.length} ${rows.length === 1 ? "entry" : "entries"} · ${uniquePatientsNonHcv} patient${uniquePatientsNonHcv === 1 ? "" : "s"}`, colSpan: 3, styles: { fontStyle: "bold", halign: "left" } },
    { content: String(totalQty), styles: { fontStyle: "bold", halign: "center" } },
    { content: formatCurrency(totalMarkup), styles: { fontStyle: "bold", halign: "right" } },
    { content: formatCurrency(totalNetFee), styles: { fontStyle: "bold", halign: "right" } },
    { content: formatCurrency(totalNetPaid), styles: { fontStyle: "bold", halign: "right" } },
  ]);

  autoTable(doc, {
    startY,
    head: [["Patient name", "Actual dispensing date", "Drug", "Qty (dispensings)", "Markup", "Fees (net $2)", "Net paid"]],
    body,
    styles: { fontSize: 9, cellPadding: 2.4, textColor: "#1B3B2E" },
    headStyles: { fillColor: "#1B3B2E", textColor: "#FFFFFF", fontStyle: "bold", halign: "left" },
    alternateRowStyles: { fillColor: "#F7F9F8" },
    // 4-column right block (72mm) so Markup / Fees / Net paid stay
    // legible. Left block shrinks to 112mm — Patient/Date/Drug still
    // fit typical pharmacy names & drug labels with autoTable wrap.
    columnStyles: {
      0: { cellWidth: 42 },
      1: { cellWidth: 30 },
      2: { cellWidth: 40 },
      3: { cellWidth: 15, halign: "center" },
      4: { cellWidth: 19, halign: "right" },
      5: { cellWidth: 19, halign: "right" },
      6: { cellWidth: 19, halign: "right" },
    },
    didParseCell: (data) => {
      if (data.column.index === 3 && data.section === "head") {
        data.cell.styles.halign = "center";
      }
      if (data.column.index >= 4 && data.section === "head") {
        data.cell.styles.halign = "right";
      }
    },
  });
}

// Roll the ledger up by canonical drug name so an auditor (or
// purchasing) can read PO quantities at a glance without counting
// rows. HCV names like "DANA EPCLUSA 400MG" collapse to "EPCLUSA" via
// canonicalDrugName; non-HCV names are kept as-is but uppercased.
// Output is a tight 3- or 4-column table positioned right under the
// main ledger; flows to a new page if there's not enough room. Column
// set switches based on isNonHcvOnly (Markup + Fees vs Acquisition cost).
function drawDrugTotalsBlock(
  doc,
  rows,
  { isNonHcvOnly, maskAcquisitionCost = false, commissionMode = null, startY: startYOverride = null },
) {
  if (!rows || rows.length === 0) return;

  const moneyForRow = commissionMode
    ? (r) => {
        const rate = commissionMode.perBottleFn(r.drug);
        if (rate == null) return null;
        return (r.bottles || 0) * rate;
      }
    : (r) => acquisitionCostFor(r.drug, r.bottles);
  const moneyLabel = commissionMode ? commissionMode.label : "Acquisition cost";

  const byDrug = new Map();
  for (const r of rows) {
    const key = canonicalDrugName(r.drug) || String(r.drug || "").toUpperCase();
    const slot = byDrug.get(key) || {
      drug: key,
      bottles: 0,
      money: 0,
      knownMoney: false,
      markup: 0,
      netFee: 0,
    };
    slot.bottles += r.bottles || 0;
    const a = moneyForRow(r);
    if (a != null) {
      slot.money += a;
      slot.knownMoney = true;
    }
    slot.markup += r.sumMarkup || 0;
    slot.netFee += r.sumNetFee || 0;
    byDrug.set(key, slot);
  }
  const summary = [...byDrug.values()].sort((a, b) => {
    if (b.bottles !== a.bottles) return b.bottles - a.bottles;
    return a.drug.localeCompare(b.drug);
  });

  const startY = startYOverride != null ? startYOverride : (doc.lastAutoTable?.finalY || 60) + 6;
  const baseOpts = {
    startY,
    styles: { fontSize: 9, cellPadding: 2.2, textColor: "#1B3B2E" },
    headStyles: { fillColor: "#2E5C46", textColor: "#FFFFFF", fontStyle: "bold", halign: "left" },
    alternateRowStyles: { fillColor: "#F7F9F8" },
    // Summary is a one-shot block — don't repeat its head on
    // continuation pages (autoTable would otherwise re-emit the
    // "Medication Totals" banner if this table splits across pages).
    showHead: "firstPage",
  };

  if (isNonHcvOnly) {
    autoTable(doc, {
      ...baseOpts,
      head: [["Medication Totals", "Qty (dispensings)", "Markup", "Fees (net $2)", "Net paid"]],
      body: summary.map((d) => [
        d.drug,
        String(d.bottles),
        formatCurrency(d.markup),
        formatCurrency(d.netFee),
        formatCurrency(d.markup + d.netFee),
      ]),
      // Align with the new 6-col main ledger (Patient / Qty / Date /
      // Drug / Fees / Money = 52+18+26+40+24+30). The summary drug
      // label spans the first two main columns.
      columnStyles: {
        0: { cellWidth: 118 },
        1: { cellWidth: 18, halign: "center" },
        2: { cellWidth: 18, halign: "right" },
        3: { cellWidth: 18, halign: "right" },
        4: { cellWidth: 18, halign: "right" },
      },
      didParseCell: (data) => {
        if (data.column.index === 1 && data.section === "head") {
          data.cell.styles.halign = "center";
        }
        if (data.column.index >= 2 && data.section === "head") {
          data.cell.styles.halign = "right";
        }
      },
    });
    return;
  }

  // HCV / mixed layout — align with new 5-col main ledger (Patient
  // 60 + Qty 22 + Date 32 + Drug 44 + Money 32 = 190mm). Summary Drug
  // label spans main's Patient+Qty+Date (114mm), Qty aligns right
  // under main's Drug column, money column matches money slot.
  if (maskAcquisitionCost) {
    autoTable(doc, {
      ...baseOpts,
      head: [["Medication Totals", "Qty (bottles)"]],
      body: summary.map((d) => [d.drug, String(d.bottles)]),
      columnStyles: {
        0: { cellWidth: 158 },
        1: { cellWidth: 32, halign: "center" },
      },
      didParseCell: (data) => {
        if (data.column.index === 1 && data.section === "head") {
          data.cell.styles.halign = "center";
        }
      },
    });
    return;
  }
  autoTable(doc, {
    ...baseOpts,
    head: [["Medication Totals", "Qty (bottles)", moneyLabel]],
    body: summary.map((d) => [
      d.drug,
      String(d.bottles),
      d.knownMoney ? formatCurrency(d.money) : "—",
    ]),
    columnStyles: {
      0: { cellWidth: 114 },
      1: { cellWidth: 44, halign: "center" },
      2: { cellWidth: 32, halign: "right" },
    },
    didParseCell: (data) => {
      if (data.column.index === 1 && data.section === "head") {
        data.cell.styles.halign = "center";
      }
      if (data.column.index === 2 && data.section === "head") {
        data.cell.styles.halign = "right";
      }
    },
  });
}

// Draw a short reconciliation block under the table so the auditor can
// eyeball-match this ledger against the dashboard. Column set switches
// based on isNonHcvOnly (Markup + Fees vs Acquisition cost).
function drawReconciliationFooter(doc, { rows, isNonHcvOnly }) {
  const uniquePatients = new Set(rows.map((r) => r.patient)).size;
  const totalQty = rows.reduce((s, r) => s + (r.bottles || 0), 0);
  const startY = (doc.lastAutoTable?.finalY || 60) + 6;

  const baseOpts = {
    startY,
    styles: { fontSize: 9, cellPadding: 2.4, textColor: "#1B3B2E", fontStyle: "bold" },
    headStyles: { fillColor: "#1B3B2E", textColor: "#FFFFFF", fontStyle: "bold", halign: "left" },
  };

  if (isNonHcvOnly) {
    const totalMarkup = rows.reduce((s, r) => s + (r.sumMarkup || 0), 0);
    const totalNetFee = rows.reduce((s, r) => s + (r.sumNetFee || 0), 0);
    const totalNetPaid = totalMarkup + totalNetFee;
    autoTable(doc, {
      ...baseOpts,
      head: [["Dashboard reconciliation", "Dispensings", "Markup", "Fees (net $2)", "Net paid"]],
      body: [[
        `Unique patients: ${uniquePatients}`,
        String(totalQty),
        formatCurrency(totalMarkup),
        formatCurrency(totalNetFee),
        formatCurrency(totalNetPaid),
      ]],
      // Column widths MUST match the Medication Totals table above
      // (118/18/18/18/18 = 190mm) so every value sits directly under
      // its equivalent column.
      columnStyles: {
        0: { cellWidth: 118 },
        1: { cellWidth: 18, halign: "center" },
        2: { cellWidth: 18, halign: "right" },
        3: { cellWidth: 18, halign: "right" },
        4: { cellWidth: 18, halign: "right" },
      },
      didParseCell: (data) => {
        if (data.column.index === 1 && data.section === "head") {
          data.cell.styles.halign = "center";
        }
        if (data.column.index >= 2 && data.section === "head") {
          data.cell.styles.halign = "right";
        }
      },
    });
    return;
  }

  // HCV / mixed layout — Acquisition cost.
  const totalAcq = rows.reduce((s, r) => {
    const a = acquisitionCostFor(r.drug, r.bottles);
    return s + (a || 0);
  }, 0);
  const hcvOnly = rows.every((r) => !!hcvDrugKey(r.drug));
  autoTable(doc, {
    ...baseOpts,
    head: [["Dashboard reconciliation", hcvOnly ? "Bottles" : "Qty", "Acquisition cost"]],
    body: [[
      `Unique patients: ${uniquePatients}`,
      String(totalQty),
      totalAcq > 0 ? formatCurrency(totalAcq) : "—",
    ]],
    // Column widths MUST match the Medication Totals table above
    // (114/44/32 = 190mm) so Bottles and Acquisition cost stack
    // directly under Qty (bottles) and the money column.
    columnStyles: {
      0: { cellWidth: 114 },
      1: { cellWidth: 44, halign: "center" },
      2: { cellWidth: 32, halign: "right" },
    },
    didParseCell: (data) => {
      if (data.column.index === 1 && data.section === "head") {
        data.cell.styles.halign = "center";
      }
      if (data.column.index === 2 && data.section === "head") {
        data.cell.styles.halign = "right";
      }
    },
  });
}

// Split a row array into { main, maviret } so the Maviret rows (AbbVie
// supplier) can be rendered on their own sheet/page. Drug detection uses
// hcvDrugKey so any variant of the brand name lands in the right bucket.
function splitBySupplier(rows) {
  const maviret = [];
  const main = [];
  for (const r of rows) {
    if (hcvDrugKey(r.drug) === "MAVIRET") {
      maviret.push(r);
    } else {
      main.push(r);
    }
  }
  return { main, maviret };
}

// Split rows into program buckets ({ "420": [...], "HepCURE": [...],
// "Unassigned": [...] }) using the caller-supplied patient → program
// map. Empty buckets are dropped by the caller. Patients not present
// in the map fall into "Unassigned" so nothing gets silently lost.
function splitByProgram(rows, programByPatient) {
  const map = programByPatient || {};
  const groups = new Map();
  for (const r of rows) {
    const key = map[r.patient] || "Unassigned";
    if (!groups.has(key)) groups.set(key, []);
    groups.get(key).push(r);
  }
  return groups;
}

// Build a stable Patient N alias map for redacted exports. Sorts
// patient names alphabetically before numbering so the mapping is
// deterministic across re-runs — an auditor comparing two masked
// PDFs will see the same "Patient 7" both times.
function buildPatientNameMask(rows) {
  const unique = Array.from(new Set(rows.map((r) => r.patient))).sort();
  const map = new Map();
  unique.forEach((name, idx) => {
    map.set(name, `Patient ${idx + 1}`);
  });
  return map;
}

// Apply the patient-name mask (if any) to a row array. Returns a fresh
// array so downstream mutations don't leak back to the caller.
function applyPatientNameMask(rows, mask) {
  if (!mask) return rows;
  return rows.map((r) => ({ ...r, patient: mask.get(r.patient) || r.patient }));
}

// Build the supplier descriptor for the main sheet subtitle. Reflects
// what's actually in the rows so an empty-of-HCV ledger doesn't lie and
// say "Supplier: Gilead".
function mainSupplierLabel(rows) {
  let hasGileadHcv = false;
  let hasNonHcv = false;
  for (const r of rows) {
    const k = hcvDrugKey(r.drug);
    if (k && k !== "MAVIRET") hasGileadHcv = true;
    if (!k) hasNonHcv = true;
  }
  if (hasGileadHcv && hasNonHcv) return "Supplier: Gilead + various (non-HCV)";
  if (hasGileadHcv) return "Supplier: Gilead";
  if (hasNonHcv) return "Supplier: various (non-HCV)";
  return "Supplier: n/a";
}

// Order program sheets consistently: George (420), Racquel (HepCURE),
// Malachi, Maria first, then anything unexpected, and the implicit
// "Unassigned" bucket last.
function orderProgramKeys(keys) {
  const order = (k) =>
    k === "420" ? 0 :
    k === "HepCURE" ? 1 :
    k === "Malachi" ? 2 :
    k === "Maria" ? 3 :
    k === "Unassigned" ? 99 :
    5;
  return [...keys].sort((a, b) => order(a) - order(b) || a.localeCompare(b));
}

export function exportOdbPaymentPdf({
  transactions,
  rangeStart,
  rangeEnd,
  scope = "all",
  programByPatient = {},
  splitMode = "supplier",
  maskPatientNames = false,
  maskAcquisitionCost = false,
}) {
  const scoped = scopeFilter(transactions || [], scope);
  // payerMap MUST be built from the full transaction set so a patient's
  // payer is classified using ALL their claims — not just the visible
  // window (otherwise borderline ODB-only patients can flip to "OTHER").
  const payerMap = buildPatientPayerMap(transactions || []);
  let rows = buildOdbRows(scoped, payerMap);
  const nameMask = maskPatientNames ? buildPatientNameMask(rows) : null;
  rows = applyPatientNameMask(rows, nameMask);

  const doc = new jsPDF({ unit: "mm", format: "letter" });
  const scopeLabel =
    scope === "hcv" ? "HCV only" : scope === "non-hcv" ? "Non-HCV only" : "All drugs";
  const periodLabel = buildOdbPeriodLabel(rows, rangeStart, rangeEnd);

  const useProgram =
    (splitMode === "420" || splitMode === "HepCURE" || splitMode === "Malachi" || splitMode === "Maria") &&
    Object.keys(programByPatient).length > 0;
  if (useProgram) {
    // Re-key the program map onto the (possibly masked) patient names
    // so the bucket lookup still lands correctly after redaction.
    const mapped = {};
    if (nameMask) {
      for (const [orig, prog] of Object.entries(programByPatient)) {
        const aliased = nameMask.get(orig);
        if (aliased) mapped[aliased] = prog;
      }
    } else {
      Object.assign(mapped, programByPatient);
    }
    // Filter to ONLY the patients assigned to the selected program.
    // Each program is run by a separate staff member — the report
    // should carry ONLY its own program's patients.
    const filtered = rows.filter((r) => mapped[r.patient] === splitMode);
    const displayName = PROGRAM_DISPLAY_NAME[splitMode] || splitMode;
    const commissionMode = commissionModeFor(splitMode);
    const title = `ODB Drug Payment Ledger — ${displayName}`;
    const subtitle = `Ontario Drug Benefit · ${formatTodayLong()} · ${scopeLabel} · ${commissionMode ? commissionMode.subtitle : `Program: ${displayName}`}`;
    renderLedgerSheet(doc, {
      title,
      subtitle,
      periodLabel,
      rows: filtered,
      dateField: "firstDispense",
      options: { maskAcquisitionCost, commissionMode },
    });
  } else {
    const { main: mainRows, maviret: mavRows } = splitBySupplier(rows);
    const sheetOpts = { maskAcquisitionCost };
    // Page 1 — Gilead + (optional) non-HCV
    renderLedgerSheet(doc, {
      title: "ODB Drug Payment Ledger",
      subtitle: `Ontario Drug Benefit · ${formatTodayLong()} · ${scopeLabel} · ${mainSupplierLabel(mainRows)}`,
      periodLabel,
      rows: mainRows,
      dateField: "firstDispense",
      options: sheetOpts,
    });
    // Page 2 — AbbVie (Maviret) — only if any Maviret rows are present
    if (mavRows.length > 0) {
      doc.addPage();
      renderLedgerSheet(doc, {
        title: "ODB Drug Payment Ledger - Maviret",
        subtitle: `Ontario Drug Benefit · ${formatTodayLong()} · ${scopeLabel} · Supplier: Sentrex`,
        periodLabel,
        rows: mavRows,
        dateField: "firstDispense",
        options: sheetOpts,
      });
    }
  }

  drawFooter(doc);
  const fname = `odb-drug-payments_${(rangeStart || "all").replace(/-/g, "")}-${(rangeEnd || "all").replace(/-/g, "")}.pdf`;
  doc.save(fname);
}

export function exportNihbPaymentPdf({
  transactions,
  rangeStart,
  rangeEnd,
  scope = "all",
  programByPatient = {},
  splitMode = "supplier",
  maskPatientNames = false,
  maskAcquisitionCost = false,
}) {
  const scoped = scopeFilter(transactions || [], scope);
  const payerMap = buildPatientPayerMap(transactions || []);
  let rows = buildNihbRows(scoped, payerMap);
  const nameMask = maskPatientNames ? buildPatientNameMask(rows) : null;
  rows = applyPatientNameMask(rows, nameMask);

  const doc = new jsPDF({ unit: "mm", format: "letter" });
  const scopeLabel =
    scope === "hcv" ? "HCV only" : scope === "non-hcv" ? "Non-HCV only" : "All drugs";
  const periodLabel = buildNihbPeriodLabel(rows, rangeStart, rangeEnd);

  const useProgram =
    (splitMode === "420" || splitMode === "HepCURE" || splitMode === "Malachi" || splitMode === "Maria") &&
    Object.keys(programByPatient).length > 0;
  if (useProgram) {
    const mapped = {};
    if (nameMask) {
      for (const [orig, prog] of Object.entries(programByPatient)) {
        const aliased = nameMask.get(orig);
        if (aliased) mapped[aliased] = prog;
      }
    } else {
      Object.assign(mapped, programByPatient);
    }
    const filtered = rows.filter((r) => mapped[r.patient] === splitMode);
    const displayName = PROGRAM_DISPLAY_NAME[splitMode] || splitMode;
    const commissionMode = commissionModeFor(splitMode);
    renderLedgerSheet(doc, {
      title: `NIHB Drug Payment Ledger — ${displayName}`,
      subtitle: `Non-Insured Health Benefits · ${formatTodayLong()} · ${scopeLabel} · ${commissionMode ? commissionMode.subtitle : `Program: ${displayName}`}`,
      periodLabel,
      rows: filtered,
      dateField: "service_date",
      options: { maskAcquisitionCost, commissionMode },
    });
  } else {
    const { main: mainRows, maviret: mavRows } = splitBySupplier(rows);
    const sheetOpts = { maskAcquisitionCost };
    renderLedgerSheet(doc, {
      title: "NIHB Drug Payment Ledger",
      subtitle: `Non-Insured Health Benefits · ${formatTodayLong()} · ${scopeLabel} · ${mainSupplierLabel(mainRows)}`,
      periodLabel,
      rows: mainRows,
      dateField: "service_date",
      options: sheetOpts,
    });
    if (mavRows.length > 0) {
      doc.addPage();
      renderLedgerSheet(doc, {
        title: "NIHB Drug Payment Ledger - Maviret",
        subtitle: `Non-Insured Health Benefits · ${formatTodayLong()} · ${scopeLabel} · Supplier: Sentrex`,
        periodLabel,
        rows: mavRows,
        dateField: "service_date",
        options: sheetOpts,
      });
    }
  }

  drawFooter(doc);
  const fname = `nihb-drug-payments_${(rangeStart || "all").replace(/-/g, "")}-${(rangeEnd || "all").replace(/-/g, "")}.pdf`;
  doc.save(fname);
}
